import { getMetadata } from '../../scripts/utils/aem-utils.js';
import { checkStBeaconPushed } from '../../scripts/utils/analytics-utils.js';
import { cookieEnabled } from '../../scripts/utils/consent-utils.js';
import { getEnvType } from '../../scripts/utils/env-utils.js';
import {
  isHandraiserLink,
  resolveHandraiserUrl,
} from '../../scripts/utils/handraiser/handraiser-link-utils.js';
import {
  getCountry,
  getCountryCode,
  getLocale,
} from '../../scripts/utils/i18n-utils.js';
import { isValidUrl } from '../../scripts/utils/link-utils.js';
import { LOCATIONS } from '../../scripts/utils/translations/constants.js';
import getI18next from '../../scripts/utils/translations/get-translations.js';
import { isChinaHost, isChinaHostname } from '../../scripts/utils/url-utils.js';
import { buildContactUsCad } from './build-contact-us-cad.js';
import {
  CHAT_CONSENT_COOKIE,
  CONTACT_AUTHOR_PATH_PATTERN,
  CONTACT_US_API_HOSTS,
  CONTACT_US_CONFIG_PATH,
  REGIONAL_LOCALES,
  SURVEY_CHAT_URL_BASE,
  UDEX_FOOTER_CONTACT_SELECTOR,
  UDEX_FOOTER_TAG,
} from './constants.js';

export function stripCountrySegment(pathname) {
  const country = getCountry();
  return country && pathname.startsWith(`/${country}/`)
    ? pathname.slice(country.length + 1)
    : pathname;
}

export function buildConfigQuery(locale, path, countryCode) {
  return `?${new URLSearchParams({ locale, path, countryCode })}`;
}

export function buildPrivacyStatementUrl() {
  const country = getCountry();
  return country
    ? `/${country}/about/legal/privacy.html`
    : '/about/legal/privacy.html';
}

/**
 * Adds missing handraiser parameters without rewriting authored query text.
 * Queryless handraiser links retain the shared resolver's absolute URL behavior.
 *
 * @param {string} url - Configured contact URL.
 * @returns {string} Enriched URL, or the original value for unsupported links.
 */
export function enrichContactUrl(url) {
  if (
    !isValidUrl(url, window.location.origin) ||
    !isHandraiserLink({ href: url })
  ) {
    return url;
  }

  const enrichedUrl = resolveHandraiserUrl(url);
  const hashIndex = url.indexOf('#');
  const beforeHash = hashIndex === -1 ? url : url.slice(0, hashIndex);
  if (!beforeHash.includes('?')) {
    return enrichedUrl;
  }

  const parsedUrl = new URL(url, window.location.origin);
  const missingParams = new URL(enrichedUrl).searchParams;
  for (const key of parsedUrl.searchParams.keys()) {
    missingParams.delete(key);
  }
  const enrichment = missingParams.toString();
  if (!enrichment) {
    return url;
  }

  const separator =
    beforeHash.endsWith('?') || beforeHash.endsWith('&') ? '' : '&';
  const fragment = hashIndex === -1 ? '' : url.slice(hashIndex);
  return `${beforeHash}${separator}${enrichment}${fragment}`;
}

/**
 * Replaces a leading AEM author prefix while preserving the origin and raw suffix.
 * China pages or destinations and en_US use root paths; other locales use the
 * existing country mapping. Unmapped locales outside China remain unchanged.
 *
 * @param {string} url - Configured contact URL.
 * @returns {string} Public vanity URL, or the original value when unsupported.
 */
export function resolveContactVanityUrl(url) {
  const match = url.match(CONTACT_AUTHOR_PATH_PATTERN);
  if (!match || !isValidUrl(url, window.location.origin)) {
    return url;
  }

  const [, origin = '', locale] = match;
  const target = new URL(url, window.location.origin);
  let country = '';
  if (
    locale.toLowerCase() !== LOCATIONS.enUS.toLowerCase() &&
    !isChinaHost() &&
    !isChinaHostname(target.hostname)
  ) {
    const localeUrl = new URL(`/${locale.toLowerCase()}/`, target);
    country = getCountry(localeUrl);
    if (!country) {
      return url;
    }
  }

  const countrySegment = country ? `${country}/` : '';
  // Keep the original suffix so query encoding, order and fragments stay intact.
  return `${origin}/${countrySegment}${url.slice(match[0].length)}`;
}

export async function resolveContactUsI18n() {
  try {
    const i18next = await getI18next();
    return (key) => i18next.t(key);
  } catch (error) {
    console.error('[ContactUs] failed to resolve translations:', error); // NOSONAR
    return (key) => key;
  }
}

export function resolveContactUsApis(envType = getEnvType()) {
  const host =
    envType === 'prod'
      ? CONTACT_US_API_HOSTS.prod
      : CONTACT_US_API_HOSTS.nonProd;
  return {
    callCenterBase: `${host}/chats/callcenter`,
    configHost: host,
    chatConfigBase: `${host}/chats/chat-brain`,
  };
}

export async function initContactUsConfig() {
  // Untranslated pages carry a hybrid page locale (e.g. de_en) that chatbrain
  // can't resolve; original-locale holds the real country_language (de_de).
  const locale = (getMetadata('original-locale') || getLocale()).toLowerCase();
  const countryCode = getCountryCode();
  const pagePath = stripCountrySegment(window.location.pathname);
  const configQuery = buildConfigQuery(locale, pagePath, countryCode);
  const i18nPromise = resolveContactUsI18n();
  const apis = resolveContactUsApis();
  const configUrl = `${apis.configHost}${CONTACT_US_CONFIG_PATH}${configQuery}`;

  return {
    locale,
    countryCode,
    pagePath,
    configUrl,
    isRegional: REGIONAL_LOCALES.has(locale),
    privacyStatementPage: buildPrivacyStatementUrl(),
    callCenterBase: apis.callCenterBase,
    chatConfigBase: `${apis.chatConfigBase}/`,
    i18n: await i18nPromise,
    consent: {
      granted: cookieEnabled(CHAT_CONSENT_COOKIE),
    },
    cad: buildContactUsCad,
    surveyChatUrlBase: SURVEY_CHAT_URL_BASE,
    ui: {
      rtl: document.documentElement.dir === 'rtl',
    },
  };
}

export function getFooterContactTarget() {
  return (
    document
      .querySelector(UDEX_FOOTER_TAG)
      ?.shadowRoot?.querySelector(UDEX_FOOTER_CONTACT_SELECTOR) ?? null
  );
}

export function forwardContactUsAnalytics(event) {
  const detail = event?.detail;
  if (!detail) {
    return;
  }

  const { bucket, ...payload } = detail;
  checkStBeaconPushed(() => {
    const dataLayer = (window.adobeDataLayer = window.adobeDataLayer || []);
    dataLayer.push(payload);
    if (payload.event && payload.event !== 'stlBeaconReady') {
      dataLayer.push({ event: 'stlBeaconReady' });
    }
  });
}

export function openConsentManager() {
  document.querySelector('#teconsent a')?.click();
}
